import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  MessageCircle,
  CalendarDays,
  MapPin,
  Box,
  CheckCircle,
  Clock,
  Package,
  Plane,
  Truck,
  Car,
  Edit,
  Trash2,
  MoreVertical,
} from 'lucide-react';
import airportImage from '../../assets/airportt.png';

const PAGE_SIZE = 12;

const formatDate = date => {
  if (!date) return 'N/A';
  let dateObj;
  if (typeof date === 'number') {
    dateObj = new Date(date);
  } else if (date && typeof date.toDate === 'function') {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
  } else {
    return 'N/A';
  }

  if (isNaN(dateObj.getTime())) {
    return 'N/A';
  }

  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = time => {
  if (!time) return 'N/A';
  if (typeof time === 'string') {
    return time;
  }
  if (time instanceof Date) {
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
  return 'N/A';
};

const getDeliveryMethodIcon = method => {
  switch (method) {
    case 'byRoad':
      return <Car className="h-4 w-4 text-gray-500" />;
    case 'byRail':
      return <Truck className="h-4 w-4 text-gray-500" />;
    case 'byAir':
      return <Plane className="h-4 w-4 text-gray-500" />;
    default:
      return <Car className="h-4 w-4 text-gray-500" />;
  }
};

const getDeliveryMethodLabel = (method, t) => {
  switch (method) {
    case 'byRoad':
      return t('travelerTripRequests.byRoad');
    case 'byRail':
      return t('travelerTripRequests.byRail');
    case 'byAir':
      return t('travelerTripRequests.byAir');
    default:
      return t('travelerTripRequests.byRoad');
  }
};

export default function MyListings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, initializing } = usePublicAuth();
  const [trips, setTrips] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingTrip, setDeletingTrip] = useState(null);
  const [openMenuTripId, setOpenMenuTripId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        // Load user's shipping trips
        const tripsQuery = query(
          collection(db, 'trips'),
          where('createdBy', '==', user.uid),
          where('tripType', '==', 'shipping'),
        );

        const tripsSnapshot = await getDocs(tripsQuery);
        const tripsData = tripsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        setTrips(tripsData);

        // Load all requests for these trips (Firestore 'in' query limit is 10)
        if (tripsData.length > 0) {
          const tripIds = tripsData.map(t => t.id);
          const allRequests = [];

          // Process in batches of 10
          for (let i = 0; i < tripIds.length; i += 10) {
            const batch = tripIds.slice(i, i + 10);
            const requestsQuery = query(
              collection(db, 'requests'),
              where('tripId', 'in', batch),
            );

            const requestsSnapshot = await getDocs(requestsQuery);
            const batchRequests = requestsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
            }));

            allRequests.push(...batchRequests);
          }

          setRequests(allRequests);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Set up real-time listeners
    if (user?.uid) {
      const tripsQuery = query(
        collection(db, 'trips'),
        where('createdBy', '==', user.uid),
        where('tripType', '==', 'shipping'),
      );

      const unsubscribeTrips = onSnapshot(tripsQuery, snapshot => {
        const tripsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTrips(tripsData);

        // Update requests when trips change (Firestore 'in' query limit is 10)
        if (tripsData.length > 0) {
          const tripIds = tripsData.map(t => t.id);
          const tripIdSet = new Set(tripIds);
          const unsubscribes = [];

          // Set up listeners for batches of 10
          for (let i = 0; i < tripIds.length; i += 10) {
            const batch = tripIds.slice(i, i + 10);
            const requestsQuery = query(
              collection(db, 'requests'),
              where('tripId', 'in', batch),
            );

            const unsubscribe = onSnapshot(requestsQuery, snapshot => {
              const requestsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
              }));

              setRequests(prev => {
                // Create a map of existing requests
                const requestMap = new Map(prev.map(r => [r.id, r]));

                // Update/add requests from this batch
                requestsData.forEach(r => {
                  if (tripIdSet.has(r.tripId)) {
                    requestMap.set(r.id, r);
                  }
                });

                // Remove requests that don't belong to current trips
                return Array.from(requestMap.values()).filter(r =>
                  tripIdSet.has(r.tripId),
                );
              });
            });

            unsubscribes.push(unsubscribe);
          }

          return () => {
            unsubscribes.forEach(unsub => unsub());
          };
        } else {
          setRequests([]);
        }
      });

      return () => unsubscribeTrips();
    }
  }, [user?.uid]);

  // Sort trips by creation date (newest first)
  const sortedTrips = useMemo(() => {
    return [...trips].sort((a, b) => {
      const aTime = a.createdAt?.toDate
        ? a.createdAt.toDate().getTime()
        : a.createdAt
        ? new Date(a.createdAt).getTime()
        : 0;
      const bTime = b.createdAt?.toDate
        ? b.createdAt.toDate().getTime()
        : b.createdAt
        ? new Date(b.createdAt).getTime()
        : 0;
      return bTime - aTime; // Newest first
    });
  }, [trips]);

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(sortedTrips.length / PAGE_SIZE));
  const paginatedTrips = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return sortedTrips.slice(startIndex, endIndex);
  }, [sortedTrips, currentPage]);

  // Pagination effect
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [sortedTrips.length, currentPage, totalPages]);

  const getRequestsForTrip = tripId => {
    return requests.filter(req => req.tripId === tripId);
  };

  const handleEditTrip = trip => {
    navigate('/list-new-trip', {
      state: {
        editMode: true,
        tripData: trip,
      },
    });
  };

  const handleDeleteTrip = async tripId => {
    if (deletingTrip) return;
    if (
      !confirm(
        t('myListings.areYouSureDeleteTrip'),
      )
    ) {
      return;
    }
    setDeletingTrip(tripId);
    try {
      const tripRef = doc(db, 'trips', tripId);
      await deleteDoc(tripRef);
      alert(t('myListings.tripDeletedSuccessfully'));
    } catch (error) {
      console.error('Error deleting trip:', error);
      alert(t('myListings.failedToDeleteTrip'));
    } finally {
      setDeletingTrip(null);
    }
  };

  const renderTripCard = trip => {
    const tripRequests = getRequestsForTrip(trip.id);
    const pendingCount = tripRequests.filter(
      r => r.status === 'pending',
    ).length;
    const acceptedCount = tripRequests.filter(
      r => r.status === 'accepted',
    ).length;
    const completedCount = tripRequests.filter(
      r => r.status === 'completed' || r.status === 'delivered',
    ).length;

    // Check if trip is completed (all requests are completed/delivered or trip status is completed)
    const isCompleted =
      trip.status === 'completed' ||
      (tripRequests.length > 0 &&
        tripRequests.every(
          r =>
            r.status === 'completed' ||
            r.status === 'delivered' ||
            r.status === 'rejected',
        ));

    return (
      <div
        className={`group relative rounded-2xl p-6 shadow-md border-2 transition-all duration-300 ${
          isCompleted
            ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 hover:border-green-400 dark:hover:border-green-600'
            : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:shadow-xl hover:border-gray-300 dark:hover:border-slate-700'
        }`}
      >
        {/* Completed Badge */}
        {isCompleted && (
          <div className="mb-4 flex items-center justify-center">
            <div className="flex items-center gap-2 px-4 py-2 bg-green-600 dark:bg-green-500 text-white dark:text-black rounded-full">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-bold">{t('myListings.completed')}</span>
            </div>
          </div>
        )}

        {/* Route Header */}
        <div className="mb-4 pb-4 border-b border-gray-100 dark:border-slate-700">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
                    {t('myListings.route')}
                  </p>
                  <p className="text-base font-bold text-gray-900 dark:text-white leading-tight truncate">
                    {trip.originDetails?.city || trip.originCity || t('home.from')}
                    {trip.originDetails?.country || trip.originCountry
                      ? `, ${trip.originDetails?.country || trip.originCountry}`
                      : ''}{' '}
                    <span className="text-gray-400 dark:text-slate-500">→</span>{' '}
                    {trip.destinationDetails?.city ||
                      trip.destinationCity ||
                      t('home.to')}
                    {trip.destinationDetails?.country || trip.destinationCountry
                      ? `, ${
                          trip.destinationDetails?.country ||
                          trip.destinationCountry
                        }`
                      : ''}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-400">
            <div className="flex items-center gap-1.5">
              <CalendarDays className="h-4 w-4 text-blue-500 dark:text-blue-400" />
              <span className="font-medium">
                {formatDate(trip.departureDate)} {t('common.at')} {' '}
                {formatTime(trip.departureTime)}
              </span>
            </div>
            {trip.deliveryMethod && (
              <div className="flex items-center gap-1.5">
                {getDeliveryMethodIcon(trip.deliveryMethod)}
                <span className="text-xs font-medium">
                  {getDeliveryMethodLabel(trip.deliveryMethod, t)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Pricing Info */}
        {(trip.pricePerUnit || trip.flatPrice) && (
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase text-yellow-700 dark:text-yellow-300">
                {t('myListings.pricing')}
              </span>
              <span className="text-sm font-bold text-yellow-900 dark:text-yellow-200">
                {trip.currency || 'USD'}{' '}
                {trip.pricingMode === 'perUnit' && trip.pricePerUnit
                  ? `${trip.pricePerUnit}/${trip.spaceUnit || 'kg'}`
                  : trip.flatPrice
                  ? trip.flatPrice
                  : 'N/A'}
              </span>
            </div>
          </div>
        )}

        {/* Available Space */}
        {trip.availableSpace && (
          <div className="mb-4 flex items-center gap-2">
            <Box className="h-4 w-4 text-gray-500 dark:text-slate-400" />
            <span className="text-xs text-gray-600 dark:text-slate-400">
              {t('myListings.available')}: {trip.availableSpace} {trip.spaceUnit || 'kg'}
            </span>
          </div>
        )}

        {/* Stats */}
        <div className="mb-4 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-700 rounded-xl">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-yellow-500 dark:text-yellow-400 flex-shrink-0" />
              <span className="text-xs font-bold text-gray-900 dark:text-white">
                {pendingCount}
              </span>
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {t('myListings.pending')}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />
              <span className="text-xs font-bold text-gray-900 dark:text-white">
                {acceptedCount}
              </span>
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {t('myListings.accepted')}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <MessageCircle className="h-4 w-4 text-gray-500 dark:text-slate-400 flex-shrink-0" />
              <span className="text-xs font-bold text-gray-900 dark:text-white">
                {tripRequests.length}
              </span>
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {t('myListings.total')}
              </span>
            </div>
            {completedCount > 0 && (
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                <span className="text-xs font-bold text-gray-900 dark:text-white">
                  {completedCount}
                </span>
                <span className="text-xs text-gray-600 dark:text-slate-400">
                  {t('myListings.done')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/traveler/trip-requests/${trip.id}`)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-black dark:bg-yellow-500 text-white dark:text-black rounded-xl text-sm font-semibold hover:bg-gray-900 dark:hover:bg-yellow-400 transition-all duration-200"
          >
            <MessageCircle className="h-4 w-4" />
            {t('myListings.view')} ({tripRequests.length})
          </button>
          {!isCompleted && (
            <div className="relative">
              <button
                onClick={() =>
                  setOpenMenuTripId(openMenuTripId === trip.id ? null : trip.id)
                }
                className="flex items-center justify-center p-2.5 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {openMenuTripId === trip.id && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setOpenMenuTripId(null)}
                  ></div>
                  <div className="absolute right-0 top-full mt-2 z-20 w-40 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                    <button
                      onClick={() => {
                        handleEditTrip(trip);
                        setOpenMenuTripId(null);
                      }}
                      disabled={deletingTrip === trip.id}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition disabled:opacity-50"
                    >
                      <Edit className="h-4 w-4" />
                      {t('myListings.edit')}
                    </button>
                    <button
                      onClick={() => {
                        handleDeleteTrip(trip.id);
                        setOpenMenuTripId(null);
                      }}
                      disabled={deletingTrip === trip.id}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition disabled:opacity-50"
                    >
                      {deletingTrip === trip.id ? (
                        <Clock className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                      {t('myListings.delete')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const isLoading = initializing || loading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />

        {/* Hero Section Skeleton */}
        <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="h-16 bg-white/20 dark:bg-slate-700/50 rounded-xl mb-4 w-3/4 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-full animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-5/6 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg w-4/6 animate-pulse"></div>
              </div>
              <div className="relative">
                <div className="w-full h-96 bg-white/20 dark:bg-slate-700/50 rounded-2xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>

        <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
          {/* Cards Skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg w-64 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-96 animate-pulse"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800"
              >
                <div className="h-6 bg-gray-200 dark:bg-slate-800 rounded-lg w-3/4 mb-4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-full mb-2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-5/6 mb-4 animate-pulse"></div>
                <div className="h-20 bg-gray-200 dark:bg-slate-800 rounded-xl mb-4 animate-pulse"></div>
                <div className="flex gap-3 mb-4">
                  <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                </div>
                <div className="h-12 bg-gray-200 dark:bg-slate-800 rounded-xl animate-pulse"></div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-6xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              {t('myListings.pleaseLogInToView')}
            </p>
            <a
              href="/auth/login"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-5 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('myListings.goToLogin')}
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('myListings.myListings')}
              </h1>
              <p className="text-xl text-yellow-50 mb-8 max-w-2xl">
                {t('myListings.manageTravelListings')}
              </p>
            </div>
            <div className="relative">
              <img
                src={airportImage}
                alt="Airport"
                className="w-full h-auto object-contain"
                style={{ maxHeight: '400px' }}
              />
            </div>
          </div>
        </div>

        {/* Decorative bottom gradient */}
        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
        {trips.length === 0 ? (
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-12 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <Box className="h-16 w-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('myListings.noListingsYet')}
            </h2>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {t('myListings.createNewTripListing')}
            </p>
            <a
              href="/list-new-trip"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-6 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('myListings.listNewTrip')}
            </a>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedTrips.map(trip => (
                <div key={trip.id}>{renderTripCard(trip)}</div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('myListings.prev')}
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                      currentPage === index + 1
                        ? 'bg-black text-yellow-400 dark:bg-yellow-500 dark:text-black'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('myListings.next')}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
